import React, { useEffect, useMemo, useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, TimeScale, Title, Tooltip, Legend);

const SERIES_COLORS = ['#005591', '#00a0e1', '#6c757d', '#002f5a', 'purple'];

export interface AsyncPanelRow {
  header?: string;
  headerKey?: string;
  value?: (data: any) => any;
  valueKey?: string;
  dangerKey?: string;
}
export interface AsyncPanelSection {
  title?: string;
  rows: AsyncPanelRow[];
}
export interface AsyncPanelChartSeries {
  name: string;
  dataKey: string;
}
export interface AsyncPanelChart {
  dataKey: string;
  xKey: string;
  series: AsyncPanelChartSeries[];
  height?: number;
}
export interface AsyncPanelLink {
  text: string;
  url: (watch: any, data?: any) => string;
  showKey?: string;
  /** Open in a new tab. Defaults to true (legacy external links). Set false for
   * internal SPA navigation. */
  newTab?: boolean;
}
export interface AsyncPanelControl {
  name: string;
  label?: string;
  type?: 'number' | 'text';
  defaultValue?: any;
  width?: number;
  suffix?: string;
}
export interface AsyncPanelAttachments {
  key: string;
  title?: string;
  download: (id: number, fileName: string) => void;
}

const resolve = (data: any, row: AsyncPanelRow): string => {
  if (row.value) return row.value(data) ?? '';
  if (row.valueKey) return data?.[row.valueKey] ?? '';
  return '';
};

/**
 * Reusable, config-driven read-only panel that fetches a single record via a
 * request and renders labeled sections, an optional line chart, footer text, and
 * an optional link — all declared through `field` config (no per-page markup).
 * Refetches whenever any of `field.watchFields` change.
 */
export const AsyncDataPanel: React.FC<any> = ({ field, watch }) => {
  const {
    request,
    requestArgs,
    watchFields = [],
    sections = [],
    chart,
    footerKeys = [],
    link,
    emptyText,
    controls = [],
    dynamicSectionsKey,
    attachments,
  }: {
    request: (...args: any[]) => Promise<any>;
    requestArgs?: (watch: any, controls?: any) => any;
    watchFields?: string[];
    sections?: AsyncPanelSection[];
    chart?: AsyncPanelChart;
    footerKeys?: string[];
    link?: AsyncPanelLink;
    emptyText?: string;
    controls?: AsyncPanelControl[];
    dynamicSectionsKey?: string;
    attachments?: AsyncPanelAttachments;
  } = field;

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [controlValues, setControlValues] = useState<any>(() =>
    controls.reduce((acc, c) => ({ ...acc, [c.name]: c.defaultValue }), {} as any),
  );

  const watchedKey = JSON.stringify((watchFields || []).map((f) => watch?.(f)));
  const controlsKey = JSON.stringify(controlValues);

  useEffect(() => {
    let cancelled = false;
    const args = requestArgs ? requestArgs(watch, controlValues) : [];
    const argArray = Array.isArray(args) ? args : [args];
    setLoading(true);
    Promise.resolve(request(...argArray))
      .then((res) => {
        if (!cancelled) setData(res ?? null);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedKey, controlsKey]);

  const chartData = useMemo(() => {
    if (!chart || !data) return null;
    const rows: any[] = data?.[chart.dataKey] ?? [];
    if (!rows.length) return null;
    const sorted = [...rows].sort(
      (a, b) => new Date(a[chart.xKey]).getTime() - new Date(b[chart.xKey]).getTime(),
    );
    return {
      datasets: chart.series.map((s, idx) => ({
        label: s.name,
        data: sorted.map((r) => ({ x: new Date(r[chart.xKey]).getTime(), y: r[s.dataKey] })),
        borderColor: SERIES_COLORS[idx % SERIES_COLORS.length],
        backgroundColor: SERIES_COLORS[idx % SERIES_COLORS.length],
        radius: 0,
        spanGaps: true,
      })),
    };
  }, [chart, data]);

  const linkUrl = link ? link.url(watch, data) : null;
  const showKeyVal = link?.showKey ? data?.[link.showKey] : undefined;
  const showLink =
    !!link && (!link.showKey || (Array.isArray(showKeyVal) ? showKeyVal.length > 0 : !!showKeyVal));
  const linkNode =
    showLink && linkUrl ? (
      <div className="mt-2 text-center">
        <NavLink to={linkUrl} target={link!.newTab === false ? undefined : '_blank'}>
          {link!.text}
        </NavLink>
      </div>
    ) : null;

  const controlsRender = controls.length > 0 && (
    <div className="d-flex align-items-end gap-3 mb-2">
      {controls.map((c) => (
        <Form.Group key={c.name}>
          {c.label && <Form.Label className="mb-1">{c.label}</Form.Label>}
          <div className="d-flex align-items-center">
            <Form.Control
              type={c.type ?? 'text'}
              size="sm"
              style={{ width: c.width ?? 80 }}
              value={controlValues[c.name] ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                const val = c.type === 'number' ? (raw === '' ? '' : Number(raw)) : raw;
                setControlValues((prev: any) => ({ ...prev, [c.name]: val }));
              }}
            />
            {c.suffix && <span className="ms-2">{c.suffix}</span>}
          </div>
        </Form.Group>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="py-2">
        {controlsRender}
        <div className="d-flex align-items-center">
          <Spinner animation="border" size="sm" className="me-2" />
          <span>Loading…</span>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="py-2">
        {controlsRender}
        {emptyText ? <div className="text-muted">{emptyText}</div> : null}
      </div>
    );
  }

  const dynamicSections: any[] | null = dynamicSectionsKey ? (data?.[dynamicSectionsKey] ?? []) : null;
  if (dynamicSectionsKey && (dynamicSections?.length ?? 0) === 0) {
    return (
      <div className="py-2">
        {controlsRender}
        {emptyText ? <div className="text-muted">{emptyText}</div> : null}
        {linkNode}
      </div>
    );
  }

  return (
    <div className="py-2">
      {controlsRender}
      <div className="row">
        {sections.map((section, si) => (
          <div className="col-md-6" key={si}>
            {section.title && <div className="fw-bold mb-2">{section.title}</div>}
            {section.rows.map((row, ri) => {
              const header = row.headerKey ? data?.[row.headerKey] : row.header;
              if (!header) return null;
              const danger = row.dangerKey ? !!data?.[row.dangerKey] : false;
              return (
                <div className="mb-1" key={ri}>
                  <span className="fw-bold me-2">{header}</span>
                  <span className={danger ? 'text-danger fw-bold' : undefined}>{resolve(data, row)}</span>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {dynamicSections && (
        <div className="row">
          {dynamicSections.map((section: any, si: number) => (
            <div className="col-md-6 mb-3" key={si}>
              {section.title && <div className="fw-bold mb-2">{section.title}</div>}
              {(section.rows ?? []).map((row: any, ri: number) => (
                <div className="mb-1" key={ri}>
                  <span className="fw-bold me-2">{row.header}</span>
                  {row.linkUrl ? (
                    <NavLink to={row.linkUrl}>{row.value}</NavLink>
                  ) : (
                    <span className={row.danger ? 'text-danger fw-bold' : undefined}>{row.value ?? ''}</span>
                  )}
                </div>
              ))}
              {section.table && (
                <table className="table table-sm table-bordered mt-2">
                  <thead>
                    <tr>
                      {(section.table.headers ?? []).map((h: string, hi: number) => (
                        <th key={hi}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(section.table.rows ?? []).map((tr: string[], tri: number) => (
                      <tr key={tri}>
                        {(tr ?? []).map((cell: string, ci: number) => (
                          <td key={ci}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      )}

      {chartData && (
        <div className="mt-3" style={{ height: chart?.height ?? 220 }}>
          <Line
            data={chartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: { x: { type: 'time', time: { unit: 'day' } } },
              plugins: { legend: { position: 'bottom' } },
            }}
          />
        </div>
      )}

      {footerKeys.map((key) =>
        data?.[key] ? (
          <div className="mt-2 text-center" key={key}>
            {data[key]}
          </div>
        ) : null,
      )}

      {attachments && (data?.[attachments.key]?.length ?? 0) > 0 && (
        <div className="mt-3">
          <div className="fw-bold mb-2">{attachments.title ?? 'Attachments'}</div>
          <table className="table table-sm table-bordered" style={{ maxWidth: 600 }}>
            <thead>
              <tr>
                <th style={{ width: 40 }} />
                <th>File Name</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {(data[attachments.key] ?? []).map((a: any) => (
                <tr key={a.id}>
                  <td>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0"
                      title="Download File"
                      onClick={() => attachments.download(a.id, a.fileName)}
                    >
                      <i className="bi bi-download" />
                    </button>
                  </td>
                  <td>{a.fileName}</td>
                  <td>{a.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {linkNode}
    </div>
  );
};

export default AsyncDataPanel;
